import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Check, ChevronRight } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { api } from '@/api/client';
import { useParametres } from '@/api/hooks';
import { LIBELLE_ETAPE, type Etape, type SituationLigne, type StatutDocument, type EcheanceCorrection } from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { cn, dateFr, montant } from '@/lib/utils';

// ------------------------------------------------------------------ « Où en sont mes documents ? » (maquette émetteur, § 4.6)
export interface DocumentResumeNiveau {
  id: string;
  numero: string | null;
  titre: string;
  statut: StatutDocument;
  etapeCourante: Etape | null;
  montant: number;
  /** Échéance de la correction attendue (document à corriger). */
  correction?: EcheanceCorrection | null;
}

/** Libellé du temps restant avant l'annulation automatique d'une correction. */
export const resteCorrection = (c: EcheanceCorrection): string =>
  c.joursOuvrablesRestants === 0 ? "dernier jour aujourd'hui" : c.joursOuvrablesRestants === 1 ? 'un jour ouvrable restant' : `${c.joursOuvrablesRestants} jours ouvrables restants`;

/**
 * Bandeau d'un document à corriger (échéance et annulation automatique, v1.1 § 5) ou annulé (motif, annulation automatique).
 */
export function BandeauCorrection({ statut, correction, motif, automatique }: { statut: StatutDocument; correction: EcheanceCorrection | null; motif: string | null; automatique: boolean }) {
  if (statut === 'A_CORRIGER' && correction) {
    return (
      <div className="flex items-start gap-2 rounded-md border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="status" data-testid="bandeau-correction">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" />
        <span>
          <span className="font-semibold">Correction attendue avant le {dateFr(correction.dateLimite)}</span> ({resteCorrection(correction)}, délai de {correction.delaiJoursOuvrables} jours ouvrables). Sans nouvelle soumission, le document sera annulé automatiquement et ses validations antérieures invalidées.
        </span>
      </div>
    );
  }
  if (statut === 'ANNULE' && motif) {
    return (
      <div className="flex items-start gap-2 rounded-md border-l-4 border-slate-500 bg-slate-100 px-3 py-2 text-sm text-slate-800" role="status" data-testid="bandeau-annulation">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" />
        <span>
          <span className="font-semibold">{automatique ? 'Annulé automatiquement.' : 'Annulé.'}</span> {motif}
          {automatique ? ' Les validations antérieures restent consultables dans le circuit, marquées invalidées.' : ''}
        </span>
      </div>
    );
  }
  return null;
}

export type CleNiveau = 'BROUILLON' | 'A_CORRIGER' | 'AUTORISE' | Etape;

/** Niveau du circuit auquel se trouve un document (brouillon, à corriger, étape en cours, autorisé). */
export const niveauDocument = (d: Pick<DocumentResumeNiveau, 'statut' | 'etapeCourante'>): CleNiveau | null => {
  if (d.statut === 'BROUILLON') return 'BROUILLON';
  if (d.statut === 'A_CORRIGER') return 'A_CORRIGER';
  if (d.statut === 'AUTORISE') return 'AUTORISE';
  if (d.statut === 'SOUMIS') return d.etapeCourante;
  return null;
};

const LIBELLE_COURT: Partial<Record<Etape, string>> = { DIRECTEUR: 'Directeur', COORDONNATEUR_MEMORIAL: 'Coord. Mémorial', CONTROLE_GESTION: 'Contrôle de gestion', DIRECTEUR_FINANCIER: 'DF', DGA_OPERATIONS: 'DGA Opérations', DGA_FINANCES: 'DGA Finances', DIRECTEUR_GENERAL: 'DG', RESPONSABLE_ACHATS: 'Resp. achats', CELLULE_MARCHES: 'Cellule des marchés' };

/**
 * Résumé par niveau en tête de la liste : brouillons, à corriger, chaque étape du circuit, autorisés ; nombre et montant ;
 * un clic sur un niveau filtre la liste.
 */
export function EntonnoirDocuments({ libelle, documents, etapes, selection, onSelection }: { libelle: string; documents: DocumentResumeNiveau[]; etapes: Etape[]; selection: CleNiveau | null; onSelection: (n: CleNiveau | null) => void }) {
  const niveaux: Array<{ cle: CleNiveau; libelle: string }> = [
    { cle: 'BROUILLON', libelle: 'Brouillons' },
    { cle: 'A_CORRIGER', libelle: 'À corriger' },
    ...etapes.map((e) => ({ cle: e as CleNiveau, libelle: LIBELLE_COURT[e] ?? LIBELLE_ETAPE[e] })),
    { cle: 'AUTORISE', libelle: 'Autorisés' },
  ];
  const par = new Map<CleNiveau, { nombre: number; montant: number }>();
  for (const d of documents) {
    const n = niveauDocument(d);
    if (!n) continue;
    const v = par.get(n) ?? { nombre: 0, montant: 0 };
    v.nombre += 1;
    v.montant += d.montant;
    par.set(n, v);
  }
  const enCours = documents.filter((d) => d.statut === 'SOUMIS');
  const totalEnCours = enCours.reduce((s, d) => s + d.montant, 0);
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div className="font-medium">Où en sont mes {libelle} ?</div>
        <div className="text-xs text-muted-foreground">
          {enCours.length} en cours de validation pour {montant(totalEnCours)} · cliquez sur un niveau pour filtrer la liste
        </div>
      </div>
      <div className="flex flex-wrap items-stretch gap-1.5">
        {niveaux.map((n, k) => {
          const v = par.get(n.cle);
          const actif = selection === n.cle;
          const alerte = n.cle === 'A_CORRIGER' && (v?.nombre ?? 0) > 0;
          return (
            <div key={n.cle} className="flex items-center gap-1.5">
              <button type="button" onClick={() => onSelection(actif ? null : n.cle)} className={cn('min-w-[7.5rem] rounded-md border px-3 py-2 text-left transition-colors hover:bg-accent', actif && 'border-primary bg-primary/5', alerte && !actif && 'border-red-200 bg-red-50/60')} aria-pressed={actif}>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{n.libelle}</div>
                <div className={cn('num text-lg font-semibold leading-tight', alerte && 'text-red-700')}>{v?.nombre ?? 0}</div>
                <div className="num text-xs text-muted-foreground">{v ? montant(v.montant) : '—'}</div>
              </button>
              {k < niveaux.length - 1 && <ChevronRight className="hidden size-3.5 shrink-0 text-muted-foreground lg:block" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Bandeaux « à corriger » en tête de l'écran de l'émetteur. */
export function BandeauxACorriger({ documents, chemin, libelle }: { documents: DocumentResumeNiveau[]; chemin: (id: string) => string; libelle: string }) {
  const aCorriger = documents.filter((d) => d.statut === 'A_CORRIGER');
  if (!aCorriger.length) return null;
  return (
    <div className="space-y-2">
      {aCorriger.map((d) => (
        <Link key={d.id} to={chemin(d.id)} className="flex items-start gap-2 rounded-md border-l-4 border-red-500 bg-red-50 px-3 py-2 text-sm text-red-900 hover:bg-red-100">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <span>
            <span className="font-semibold">{d.numero ?? `${libelle} sans numéro`} à corriger.</span> {d.titre}
            {d.correction && <span className="block text-xs">À soumettre avant le {dateFr(d.correction.dateLimite)} ({resteCorrection(d.correction)}) ; passé ce délai, annulation automatique.</span>}
          </span>
        </Link>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ activité → sous-activité (v1.1, § 7)
const NON_CLASSEE = { code: 'NC', libelle: 'Non classée' };
const activiteDe = (l: SituationLigne) => ({ code: l.activiteCode ?? NON_CLASSEE.code, libelle: l.activiteLibelle ?? NON_CLASSEE.libelle });

/**
 * Rattachement budgétaire en deux niveaux : l'utilisateur choisit l'activité, puis la sous-activité (ligne budgétaire) ;
 * la liste des sous-activités est filtrée par l'activité et la situation budgétaire se récupère automatiquement.
 */
export function ActiviteSousActivite({ lignes, ligneId, setLigneId, obligatoire, prefixe = 'ligne', libelleSousActivite = 'Sous-activité' }: { lignes: SituationLigne[]; ligneId: string; setLigneId: (id: string) => void; obligatoire?: ReactNode; prefixe?: string; libelleSousActivite?: string }) {
  const ligneChoisie = lignes.find((l) => l.id === ligneId);
  const [activite, setActivite] = useState<string>('');
  useEffect(() => {
    if (ligneChoisie) setActivite(activiteDe(ligneChoisie).code);
  }, [ligneChoisie]);
  const activites = [...new Map(lignes.map((l) => [activiteDe(l).code, activiteDe(l)])).values()].sort((a, b) => a.code.localeCompare(b.code));
  const uneSeule = activites.length === 1;
  const activiteCourante = activite || (uneSeule ? activites[0]!.code : '');
  const sousActivites = lignes.filter((l) => !activiteCourante || activiteDe(l).code === activiteCourante);
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <div className="space-y-1.5">
        <Label htmlFor={`${prefixe}-activite`}>Activité{obligatoire}</Label>
        <SelectNatif
          id={`${prefixe}-activite`}
          value={activiteCourante}
          onChange={(e) => {
            setActivite(e.target.value);
            if (ligneChoisie && activiteDe(ligneChoisie).code !== e.target.value) setLigneId('');
          }}
        >
          <option value="">Choisir l'activité</option>
          {activites.map((a) => (
            <option key={a.code} value={a.code}>
              {a.code} · {a.libelle}
            </option>
          ))}
        </SelectNatif>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={prefixe}>{libelleSousActivite}{obligatoire}</Label>
        <SelectNatif id={prefixe} value={ligneId} onChange={(e) => setLigneId(e.target.value)} disabled={!activiteCourante && activites.length > 1}>
          <option value="">{activiteCourante || uneSeule ? 'Choisir la sous-activité' : "Choisir d'abord l'activité"}</option>
          {sousActivites.map((l) => (
            <option key={l.id} value={l.id}>
              {l.code} · {l.libelle} · disponible {montant(l.disponiblePrevisionnel)}
            </option>
          ))}
        </SelectNatif>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ rattachement budgétaire (maquette, § 4.1)
/** Centre de coût et disponible de la ligne choisie : prévu, réalisé, engagé en cours, disponible. */
/**
 * Situation de la ligne choisie à l'initiation d'un document (spécifications complémentaires v1.1, § 6.3) : budget,
 * engagé et payé, réservé par les dossiers en circuit, solde réellement disponible, montant du document et solde
 * prévisionnel après soumission ; alerte quand le montant ne tient pas dans le disponible.
 */
export function SituationLigneChoisie({ ligne, montantDocument = 0, montantInitial = 0 }: { ligne: SituationLigne | undefined; montantDocument?: number; montantInitial?: number }) {
  if (!ligne) return null;
  // Un document déjà en circuit est compté dans « réservé » : on ne le déduit pas deux fois.
  const reserve = Math.max(0, ligne.enCircuit - montantInitial);
  const disponibleReel = ligne.disponible - reserve;
  const apres = disponibleReel - montantDocument;
  const depasseDisponible = montantDocument > ligne.disponible;
  const depasseReel = !depasseDisponible && montantDocument > disponibleReel;
  const cases: Array<[string, number, string?, string?]> = [
    ['Budget de la ligne', ligne.budgetActualise, 'prévisionnel de l’exercice'],
    ['Engagé · payé', ligne.engage, `dont ${montant(ligne.paye)} payés`],
    ['Réservé par les dossiers en circuit', reserve, 'TDR, FED et bons non encore engagés'],
    ['Solde réellement disponible', disponibleReel, 'budget − engagé − réservé', disponibleReel < 0 ? 'text-red-700' : 'text-emerald-700'],
    ['Montant de ce document', montantDocument, 'calculé sur les saisies'],
    ['Solde après soumission', apres, 'disponible réel − ce document', apres < 0 ? 'text-red-700' : 'text-emerald-700'],
  ];
  return (
    <div className="rounded-md border bg-muted/30 p-3" data-testid="situation-ligne">
      <div className="mb-2 text-[11px] uppercase tracking-wide text-muted-foreground">
        Activité <span className="font-semibold text-foreground">{ligne.activiteLibelle ?? 'Non classée'}</span> · sous-activité <span className="num font-semibold text-foreground">{ligne.code}</span> · {ligne.libelle}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {cases.map(([l, v, d, c]) => (
          <div key={l}>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{l}</div>
            <div className={cn('num text-sm font-semibold', c)}>{montant(v)}</div>
            {d && <div className="text-[11px] text-muted-foreground">{d}</div>}
          </div>
        ))}
      </div>
      {depasseDisponible && (
        <div className="mt-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800" role="alert">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <span>
            Insuffisance budgétaire : le montant dépasse le disponible de la ligne de {montant(montantDocument - ligne.disponible)}. La soumission sera refusée ; réduire le montant, choisir une autre sous-activité ou demander un virement budgétaire.
          </span>
        </div>
      )}
      {depasseReel && (
        <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800" role="alert">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <span>Le montant tient dans le disponible mais pas dans le solde réellement disponible : les dossiers déjà en circuit sur cette ligne épuiseraient le budget. Les validateurs en seront avertis.</span>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ circuit prévu (simulation)
export function useCircuitSimule(type: 'TDR' | 'FED' | 'REQUISITION', montant: number, operationnel: boolean, structure: string, memorial: boolean) {
  const arrondi = Math.max(0, Math.round(montant));
  return useQuery({ queryKey: ['circuits', 'simuler', type, arrondi, operationnel, structure, memorial] as const, queryFn: () => api<{ etapes: Etape[] }>(`/circuits/simuler?type=${type}&montant=${arrondi}&operationnel=${operationnel}&structure=${encodeURIComponent(structure)}&memorial=${memorial}`), staleTime: 60_000 });
}

/** Direction gestionnaire du Mémorial (paramètre) : la case « activité du Mémorial » n'est proposée qu'à ses émetteurs ; vide, à tous. */
export function useDeclareMemorial(structureCode: string): boolean {
  const parametres = useParametres();
  const gestionnaire = String(parametres.data?.find((p) => p.cle === 'circuit.memorialStructure')?.valeur ?? 'REP').trim().toUpperCase();
  return gestionnaire === '' || gestionnaire === structureCode.toUpperCase();
}

/** Circuit que suivra le document, selon le montant, la direction émettrice, l'activité du Mémorial et les seuils de délégation. */
export function CircuitPrevu({ type, montant, operationnel, structure, memorial }: { type: 'TDR' | 'FED' | 'REQUISITION'; montant: number; operationnel: boolean; structure: string; memorial: boolean }) {
  const q = useCircuitSimule(type, montant, operationnel, structure, memorial);
  const etapes = q.data?.etapes ?? [];
  return (
    <div className="text-xs text-muted-foreground">
      <span className="font-medium text-foreground">{type === 'REQUISITION' ? 'Circuit de cette réquisition : ' : `Circuit de ce ${type} : `}</span>
      {etapes.length ? etapes.map((e) => LIBELLE_ETAPE[e] ?? e).join(' → ') : q.isLoading ? 'calcul…' : 'selon le montant et les seuils de délégation'}
    </div>
  );
}

// ------------------------------------------------------------------ complétude
export interface PointCompletude {
  ok: boolean;
  message: string;
  etape?: number;
  /** Simple avertissement : n'empêche pas la soumission. */
  avertissement?: boolean;
}

/** Liste de complétude : points manquants d'abord, puis les points satisfaits (repliés au besoin). */
export function Completude({ points, onEtape, compact }: { points: PointCompletude[]; onEtape?: (etape: number) => void; compact?: boolean }) {
  const manquants = points.filter((p) => !p.ok);
  const faits = points.filter((p) => p.ok).length;
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-medium">Complétude</div>
        <Badge variant={manquants.some((m) => !m.avertissement) ? 'secondary' : 'default'} className="num">
          {faits}/{points.length}
        </Badge>
      </div>
      {manquants.length === 0 ? (
        <div className="flex items-center gap-2 text-xs text-emerald-700">
          <Check className="size-4" /> Tout est renseigné : le document peut être soumis.
        </div>
      ) : (
        <ul className="space-y-1">
          {(compact ? manquants.slice(0, 6) : manquants).map((p, k) => (
            <li key={k} className={cn('flex items-start gap-2 text-xs', p.avertissement ? 'text-amber-700' : 'text-red-700')}>
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
              {onEtape && p.etape ? (
                <button type="button" className="text-left hover:underline" onClick={() => onEtape(p.etape!)}>
                  {p.message} (étape {p.etape})
                </button>
              ) : (
                <span>{p.message}</span>
              )}
            </li>
          ))}
          {compact && manquants.length > 6 && <li className="text-xs text-muted-foreground">… et {manquants.length - 6} autre(s).</li>}
        </ul>
      )}
    </div>
  );
}

/** Carte latérale (budget, documents générés, circuit) des formulaires de l'émetteur. */
export function Lateral({ titre, children }: { titre?: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      {titre && <div className="mb-2 text-sm font-medium">{titre}</div>}
      {children}
    </div>
  );
}

// ------------------------------------------------------------------ montant en lettres (FED)
const UNITES = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];
const DIZAINES = ['', 'dix', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', 'soixante', 'quatre-vingt', 'quatre-vingt'];

function moinsDeCent(n: number): string {
  if (n < 20) return UNITES[n]!;
  const d = Math.floor(n / 10);
  const u = n % 10;
  if (d === 7 || d === 9) {
    const reste = 10 + u;
    return `${DIZAINES[d]}${reste === 11 && d === 7 ? ' et ' : '-'}${UNITES[reste]}`;
  }
  if (u === 0) return d === 8 ? 'quatre-vingts' : DIZAINES[d]!;
  if (u === 1 && d !== 8) return `${DIZAINES[d]} et un`;
  return `${DIZAINES[d]}-${UNITES[u]}`;
}

function moinsDeMille(n: number): string {
  const c = Math.floor(n / 100);
  const r = n % 100;
  const centaines = c === 0 ? '' : c === 1 ? 'cent' : `${UNITES[c]} cent${r === 0 ? 's' : ''}`;
  return [centaines, r ? moinsDeCent(r) : ''].filter(Boolean).join(' ');
}

function entierEnLettres(n: number): string {
  if (n === 0) return 'zéro';
  const parties: string[] = [];
  const groupes: Array<[number, string, string]> = [
    [1_000_000_000, 'milliard', 'milliards'],
    [1_000_000, 'million', 'millions'],
    [1000, 'mille', 'mille'],
  ];
  let reste = n;
  for (const [valeur, sing, plur] of groupes) {
    const q = Math.floor(reste / valeur);
    if (q) {
      parties.push(valeur === 1000 && q === 1 ? 'mille' : `${moinsDeMille(q)} ${q > 1 ? plur : sing}`);
      reste %= valeur;
    }
  }
  if (reste) parties.push(moinsDeMille(reste));
  return parties.join(' ');
}

const DEVISES: Record<string, [string, string, string, string]> = { USD: ['dollar américain', 'dollars américains', 'cent', 'cents'], CDF: ['franc congolais', 'francs congolais', 'centime', 'centimes'], EUR: ['euro', 'euros', 'centime', 'centimes'] };

/** Montant en toutes lettres, généré automatiquement (maquette : « Mille deux cent quarante dollars américains »). */
export function montantEnLettres(n: number, devise = 'USD'): string {
  if (!Number.isFinite(n) || n < 0) return '';
  const [sing, plur, cSing, cPlur] = DEVISES[devise] ?? [devise, devise, 'cent', 'cents'];
  const entier = Math.floor(n + 1e-9);
  const cents = Math.round((n - entier) * 100);
  let texte = `${entierEnLettres(entier)} ${entier > 1 ? plur : sing}`;
  if (cents > 0) texte += ` et ${entierEnLettres(cents)} ${cents > 1 ? cPlur : cSing}`;
  return texte.charAt(0).toUpperCase() + texte.slice(1) + '.';
}
